import {
  createInviteSchema,
  type CreatedInvite,
  type CustomRole,
  type Team,
  type TeamMember,
} from '@nexo/types';
import {
  Avatar,
  Badge,
  Button,
  Field,
  IconButton,
  Input,
  Modal,
  SegmentedControl,
  Select,
  cn,
  useToast,
} from '@nexo/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Check,
  Copy,
  Crown,
  LogOut,
  MessageSquare,
  MoreHorizontal,
  Shield,
  ShieldOff,
  Trash2,
  UserMinus,
} from 'lucide-react';
import { useState, type ReactNode, type SubmitEvent } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useTeam } from '../../layouts/TeamContext';
import { api } from '../../lib/api';
import { cache } from '../../lib/cache';
import { InviteLinks } from './InviteLinks';
import { RolesManager } from './RolesManager';
import { UserBadges } from '../status/UserBadges';
import { RoleChip, permissionLabel } from './roles';
import { errorMessage, validate } from '../../lib/forms';
import { keys } from '../../lib/queries';
import { useMe } from '../auth/AuthProvider';
import { can } from '../../lib/permissions';

export function MembersDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { team, members, roles } = useTeam();
  const [tab, setTab] = useState<'members' | 'roles'>('members');
  const canInvite = can(team, 'invite_members');
  const { t } = useTranslation();

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('Miembros de {{team}}', { team: team.name })}
      size="md"
    >
      <SegmentedControl
        label={t('Sección')}
        value={tab}
        onChange={setTab}
        className="mb-4"
        options={[
          { value: 'members', label: t('Miembros'), count: members.length },
          { value: 'roles', label: t('Roles'), count: roles.length },
        ]}
      />
      {tab === 'members' ? (
        <>
          {canInvite && <InviteLinks />}
          {canInvite && <InviteForm />}
          <MemberList onClose={onClose} />
        </>
      ) : (
        <RolesManager />
      )}
    </Modal>
  );
}

function MemberList({ onClose }: { onClose: () => void }) {
  const { team, members, online, roleById } = useTeam();
  const me = useMe();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const { t } = useTranslation();

  const openDm = useMutation({
    mutationFn: (userId: string) => api.channels.openDm(team.id, userId),
    onSuccess: (channel) => {
      cache.upsertChannel(queryClient, channel);
      onClose();
      void navigate(`/app/t/${team.id}/c/${channel.id}`);
    },
  });

  const [managing, setManaging] = useState<string | null>(null);

  const leave = useMutation({
    mutationFn: () => api.teams.leave(team.id),
    onSuccess: () => {
      queryClient.setQueryData<Team[]>(keys.teams, (list) =>
        list?.filter((other) => other.id !== team.id),
      );
      onClose();
      void navigate('/app/home');
    },
    onError: (err) =>
      toast({ title: t('No puedes salir'), description: errorMessage(err), tone: 'error' }),
  });

  return (
    <>
      <ul className="flex flex-col divide-y divide-border">
        {members.map((member) => {
          const memberRoles = member.customRoleIds
            .map((id) => roleById(id))
            .filter((r): r is CustomRole => Boolean(r));
          return (
            <li key={member.id} className="py-2.5">
              <div className="flex items-center gap-3">
                <Avatar
                  id={member.id}
                  name={member.displayName}
                  src={member.avatarUrl}
                  online={online ? online.has(member.id) : undefined}
                />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">
                    {member.displayName}{' '}
                    {member.id === me.id && <span className="text-muted">{t('(tú)')}</span>}
                    <UserBadges user={member} className="ml-1" />
                  </p>
                  {member.title && <p className="truncate text-xs text-muted">{member.title}</p>}
                  {memberRoles.length > 0 && (
                    <div className="mt-1 flex flex-wrap gap-1">
                      {memberRoles.map((role) => (
                        <RoleChip key={role.id} role={role} />
                      ))}
                    </div>
                  )}
                </div>
                <Badge tone={member.role === 'member' ? 'neutral' : 'accent'}>
                  {t(permissionLabel[member.role])}
                </Badge>
                {member.id !== me.id && (
                  <IconButton
                    label={t('Mensaje a {{name}}', { name: member.displayName })}
                    size="sm"
                    onClick={() => openDm.mutate(member.id)}
                  >
                    <MessageSquare className="size-4" />
                  </IconButton>
                )}
                {(team.role !== 'member' ||
                  can(team, 'manage_roles') ||
                  can(team, 'kick_members')) && (
                  <IconButton
                    label={t('Gestionar a {{name}}', { name: member.displayName })}
                    size="sm"
                    active={managing === member.id}
                    onClick={() => setManaging(managing === member.id ? null : member.id)}
                  >
                    <MoreHorizontal className="size-4" />
                  </IconButton>
                )}
              </div>
              {managing === member.id && (
                <MemberMenu member={member} onDone={() => setManaging(null)} />
              )}
            </li>
          );
        })}
      </ul>
      {team.role !== 'owner' && (
        <div className="mt-4 flex justify-end border-t border-border pt-4">
          <Button
            variant="ghost"
            size="sm"
            icon={<LogOut className="size-4" />}
            loading={leave.isPending}
            onClick={() => {
              if (
                window.confirm(
                  t('¿Salir de {{team}}? Perderás el acceso a sus canales.', { team: team.name }),
                )
              )
                leave.mutate();
            }}
          >
            {t('Salir del equipo')}
          </Button>
        </div>
      )}
    </>
  );
}

/** Admin actions on a member: custom roles, permission level, ownership and removal. */
function MemberMenu({ member, onDone }: { member: TeamMember; onDone: () => void }) {
  const { team, roles } = useTeam();
  const me = useMe();
  const queryClient = useQueryClient();
  const toast = useToast();

  const { t } = useTranslation();

  const isSelf = member.id === me.id;
  // Owners manage everyone; admins (and «Expulsar miembros») only plain members.
  const outranks =
    !isSelf && member.role !== 'owner' && (team.role === 'owner' || member.role === 'member');
  const canPromote = outranks && team.role !== 'member';
  const canKick = outranks && can(team, 'kick_members');
  const canAssignRoles = can(team, 'manage_roles');

  const onError = (err: unknown) =>
    toast({ title: t('No se pudo guardar'), description: errorMessage(err), tone: 'error' });
  const replaceMember = (updated: TeamMember) =>
    queryClient.setQueryData<TeamMember[]>(keys.members(team.id), (list) =>
      list?.map((m) => (m.id === updated.id ? updated : m)),
    );

  const setRoles = useMutation({
    mutationFn: (roleIds: string[]) => api.teams.setMemberRoles(team.id, member.id, roleIds),
    onSuccess: (updated) => {
      replaceMember(updated);
      void queryClient.invalidateQueries({ queryKey: keys.roles(team.id) });
    },
    onError,
  });
  const setPermission = useMutation({
    mutationFn: (role: 'admin' | 'member') => api.teams.updateMemberRole(team.id, member.id, role),
    onSuccess: replaceMember,
    onError,
  });
  const transfer = useMutation({
    mutationFn: () => api.teams.transferOwnership(team.id, member.id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.members(team.id) });
      void queryClient.invalidateQueries({ queryKey: keys.teams });
      toast({
        title: t('{{name}} es ahora el propietario', { name: member.displayName }),
        tone: 'success',
      });
    },
    onError,
  });
  const remove = useMutation({
    mutationFn: () => api.teams.removeMember(team.id, member.id),
    onSuccess: () =>
      queryClient.setQueryData<TeamMember[]>(keys.members(team.id), (list) =>
        list?.filter((m) => m.id !== member.id),
      ),
    onError,
  });

  const toggleRole = (roleId: string) => {
    const has = member.customRoleIds.includes(roleId);
    setRoles.mutate(
      has ? member.customRoleIds.filter((id) => id !== roleId) : [...member.customRoleIds, roleId],
    );
  };

  return (
    <div className="mt-2 ml-12 flex flex-col rounded-xl border border-border bg-surface-2 p-1.5 text-sm">
      <p className="px-2 pt-1 pb-1.5 text-[11px] font-semibold tracking-wider text-subtle uppercase">
        {t('Roles')}
      </p>
      {!canAssignRoles ? (
        <p className="px-2 pb-2 text-xs text-muted">{t('No puedes asignar roles.')}</p>
      ) : roles.length === 0 ? (
        <p className="px-2 pb-2 text-xs text-muted">
          {t('Aún no hay roles. Créalos en la pestaña «Roles».')}
        </p>
      ) : (
        roles.map((role) => (
          <label
            key={role.id}
            className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-surface"
          >
            <input
              type="checkbox"
              checked={member.customRoleIds.includes(role.id)}
              disabled={setRoles.isPending}
              onChange={() => toggleRole(role.id)}
              className="accent-[var(--color-accent)]"
            />
            <RoleChip role={role} />
          </label>
        ))
      )}
      {(canPromote || canKick) && <div className="my-1 border-t border-border" />}
      {canPromote && (
        <>
          <MenuItem
            icon={member.role === 'admin' ? <ShieldOff /> : <Shield />}
            onClick={() => {
              setPermission.mutate(member.role === 'admin' ? 'member' : 'admin');
              onDone();
            }}
          >
            {member.role === 'admin' ? t('Quitar administrador') : t('Hacer administrador')}
          </MenuItem>
          {team.role === 'owner' && (
            <MenuItem
              icon={<Crown />}
              onClick={() => {
                onDone();
                if (
                  window.confirm(
                    t(
                      '¿Transferir la propiedad de {{team}} a {{name}}? Pasarás a ser administrador.',
                      { team: team.name, name: member.displayName },
                    ),
                  )
                )
                  transfer.mutate();
              }}
            >
              {t('Transferir propiedad')}
            </MenuItem>
          )}
        </>
      )}
      {canKick && (
        <>
          <MenuItem
            danger
            icon={<UserMinus />}
            onClick={() => {
              onDone();
              if (
                window.confirm(
                  t('¿Expulsar a {{name}} de {{team}}?', {
                    name: member.displayName,
                    team: team.name,
                  }),
                )
              )
                remove.mutate();
            }}
          >
            {t('Expulsar del equipo')}
          </MenuItem>
        </>
      )}
    </div>
  );
}

function MenuItem({
  icon,
  danger,
  onClick,
  children,
}: {
  icon: ReactNode;
  danger?: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex items-center gap-2 rounded-lg px-2 py-1.5 text-left hover:bg-surface [&_svg]:size-4',
        danger ? 'text-danger' : 'text-text',
      )}
    >
      {icon}
      {children}
    </button>
  );
}

function InviteForm() {
  const { team } = useTeam();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'member' | 'admin'>('member');
  const [error, setError] = useState<string>();
  const [created, setCreated] = useState<CreatedInvite | null>(null);
  const [copied, setCopied] = useState(false);
  const { t } = useTranslation();

  const invites = useQuery({
    queryKey: keys.teamInvites(team.id),
    queryFn: () => api.teams.invites(team.id),
  });

  const invite = useMutation({
    mutationFn: () => api.teams.invite(team.id, { email, role }),
    onSuccess: (result) => {
      setCreated(result);
      setEmail('');
      setCopied(false);
      void queryClient.invalidateQueries({ queryKey: keys.teamInvites(team.id) });
    },
    onError: (err) => setError(errorMessage(err)),
  });

  const revoke = useMutation({
    mutationFn: (inviteId: string) => api.teams.revokeInvite(team.id, inviteId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: keys.teamInvites(team.id) }),
  });

  function onSubmit(event: SubmitEvent) {
    event.preventDefault();
    const { errors } = validate(createInviteSchema, { email, role });
    if (errors) return setError(errors.email);
    invite.mutate();
  }

  async function copy(url: string) {
    await navigator.clipboard.writeText(url);
    setCopied(true);
    toast({ title: t('Enlace copiado'), tone: 'success' });
  }

  return (
    <section className="mb-5 rounded-xl border border-border bg-surface-2 p-4">
      <h3 className="mb-3 text-sm font-semibold">{t('Invitar por email')}</h3>
      <form
        onSubmit={onSubmit}
        noValidate
        className="flex flex-col gap-2 sm:flex-row sm:items-start"
      >
        <Field label={t('Email')} error={error} className="flex-1 [&>label]:sr-only">
          {(props) => (
            <Input
              {...props}
              type="email"
              placeholder={t('nombre@empresa.com')}
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setError(undefined);
              }}
            />
          )}
        </Field>
        <Select
          aria-label={t('Rol')}
          value={role}
          onChange={setRole}
          className="sm:w-32"
          options={[
            { value: 'member', label: t('Miembro') },
            {
              value: 'admin',
              label: t('Admin'),
              description: t('Puede gestionar todo el equipo'),
            },
          ]}
        />
        <Button type="submit" loading={invite.isPending}>
          {t('Invitar')}
        </Button>
      </form>

      {created && (
        <div className="mt-3 rounded-lg bg-surface p-3 text-sm">
          <p className="mb-2 text-muted">
            {created.emailSent ? (
              <Trans
                i18nKey="Hemos enviado la invitación por email a <strong>{{email}}</strong>. También puedes compartirle este enlace:"
                values={{ email: created.email }}
                components={{ strong: <strong className="text-text" /> }}
              />
            ) : (
              <Trans
                i18nKey="Invitación creada para <strong>{{email}}</strong>, pero no se pudo enviar el email. Compártele este enlace:"
                values={{ email: created.email }}
                components={{ strong: <strong className="text-text" /> }}
              />
            )}
          </p>
          <div className="flex gap-2">
            <Input
              readOnly
              value={created.url}
              onFocus={(e) => e.target.select()}
              className="font-mono text-xs"
            />
            <Button
              variant="secondary"
              icon={copied ? <Check className="size-4" /> : <Copy className="size-4" />}
              onClick={() => void copy(created.url)}
            >
              {t('Copiar')}
            </Button>
          </div>
        </div>
      )}

      {invites.data && invites.data.length > 0 && (
        <div className="mt-4">
          <p className="mb-1 text-xs font-medium tracking-wide text-muted uppercase">
            {t('Pendientes', { context: 'invites' })}
          </p>
          <ul className="flex flex-col">
            {invites.data.map((inv) => (
              <li key={inv.id} className="flex items-center justify-between gap-2 py-1 text-sm">
                <span className="truncate">{inv.email}</span>
                <IconButton
                  label={t('Revocar invitación a {{email}}', { email: inv.email })}
                  size="sm"
                  onClick={() => revoke.mutate(inv.id)}
                >
                  <Trash2 className="size-4" />
                </IconButton>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}
